import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Select, Button, Pagination, Alert, message, Spin, Tooltip, Modal, Tag, Popover, Drawer, Timeline, Badge, Image } from 'antd';
import { SyncOutlined, CheckCircleOutlined, CloseCircleOutlined, CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import request, { BASE_URL } from '../../../utils/request';
import { copyToClipboard } from '../../../utils/clipboard';
import { syncCorp, initiateAuth, getAuthUrl } from '../../../api/corp';
import { useSuite } from '../../../context/SuiteContext';
import { QRCodeSVG } from 'qrcode.react';
import './WeComList.css';
import EmployeeList from '../Employees/EmployeeList';

const { Option } = Select;

const WeComList = () => {
    const [loading, setLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const { suiteId, suiteList } = useSuite();

    // Auth Modal state
    const [authModalVisible, setAuthModalVisible] = useState(false);
    const [currentSuite, setCurrentSuite] = useState(null);
    const [authUrl, setAuthUrl] = useState('');
    const [polling, setPolling] = useState(false);
    const [authLoading, setAuthLoading] = useState(false); // For showing success loading icon

    // Progress Modal state
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [progressRecord, setProgressRecord] = useState(null);

    // Employee Drawer state
    const [employeeDrawerVisible, setEmployeeDrawerVisible] = useState(false);
    const [activeCorpRecord, setActiveCorpRecord] = useState(null);

    // Authorization Guide Drawer state
    const [guideDrawerVisible, setGuideDrawerVisible] = useState(false);

    // Search filters
    const [filters, setFilters] = useState({
        corp_id: '',
        suite_auth_status: undefined,
        corp_arch_auth: undefined,
        customer_assistant_auth: undefined,
    });

    const fetchData = async (page = 1, size = 10, silent = false) => {
        if (!suiteId) return; // Wait for suiteId to be loaded

        if (!silent) setLoading(true);
        try {
            const params = {
                page: page,
                page_size: size,
                suite_id: suiteId,
                ...filters,
            };

            // Remove undefined filters
            Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

            const res = await request.get('/corp/get_list', { params });
            if (res.code === 0) {
                setData(res.data.list);
                setTotal(res.data.total);
            } else {
                message.error(res.message);
            }
        } catch (error) {
            console.error('Fetch list failed:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (suiteId) {
            fetchData(currentPage, pageSize);
            // Update current suite info
            const suite = suiteList.find(s => s.suite_id === suiteId);
            setCurrentSuite(suite);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suiteId, suiteList]); // Re-fetch when suiteId changes

    // Polling removed as per new requirement (Modal flow)

    const handleSearch = () => {
        setCurrentPage(1);
        fetchData(1, pageSize);
    };

    const handleReset = () => {
        setFilters({
            corp_id: '',
            suite_auth_status: undefined,
            corp_arch_auth: undefined,
            customer_assistant_auth: undefined,
        });
        setCurrentPage(1);
        // Need to use the reset state for fetching, so passing empty/undefined directly or relying on state update (which is async)
        // Here we manually pass reset values to fetchData for immediate effect
        setLoading(true);
        request.get('/corp/get_list', { params: { page: 1, page_size: pageSize, suite_id: suiteId } })
            .then(res => {
                if (res.code === 0) {
                    setData(res.data.list);
                    setTotal(res.data.total);
                }
            })
            .finally(() => setLoading(false));
    };

    const onPageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
        fetchData(page, size);
    };

    const isAuthorized = (record) => {
        if (!record) return false;
        // 1. arch_auth_status: 1
        // 2. externalcontact_auth_status: 1
        // 3. contact_auth_status: 1
        // 4. customer_assistant_auth: 1
        // 5. view_range: 1 or 2 or 3
        // 6. view_range_detail: not empty
        const { arch_auth_status, externalcontact_auth_status, contact_auth_status, customer_assistant_auth, view_range, view_range_detail } = record;
        return arch_auth_status === 1 &&
            externalcontact_auth_status === 1 &&
            contact_auth_status === 1 &&
            customer_assistant_auth === 1 &&
            (view_range === 1 || view_range === 2 || view_range === 3) &&
            view_range_detail;
    };

    const checkAuth = (record) => {
        if (record.suite_auth_status !== 1) {
            message.warning('应用未授权！');
            return false;
        }
        if (!isAuthorized(record)) {
            message.warning('请确保所有授权配置均已完成！');
            return false;
        }
        return true;
    };

    const navigate = useNavigate();

    const handleMemberManage = (record) => {
        if (!checkAuth(record)) return;

        setActiveCorpRecord(record);
        setEmployeeDrawerVisible(true);
    };

    const handleSync = async (record) => {
        if (!checkAuth(record)) return;

        const { corp_id, suite_id } = record;
        const storageKey = `last_sync_time_${corp_id}`;
        const lastSyncTime = localStorage.getItem(storageKey);
        const now = Date.now();

        if (lastSyncTime && now - parseInt(lastSyncTime, 10) < 1 * 60 * 1000) {
            message.warning('同步企业信息最快间隔1分钟，请稍后重试！');
            return;
        }

        setSyncLoading(true);
        try {
            // API expects corp_id
            const res = await syncCorp({ corp_id, suite_id });
            if (res.code === 0) {
                message.success('更新成功');
                localStorage.setItem(storageKey, now.toString());
                // Refresh list to show updated info
                fetchData(currentPage, pageSize);
            } else {
                message.error(res.message || '更新失败');
            }
        } catch (error) {
            // Error is handled in request.js or here
            console.error('Sync failed:', error);
            message.error(error.message || '更新失败');
        } finally {
            setSyncLoading(false);
        }
    };

    const handleAuthClick = async () => {
        if (!suiteId) {
            message.warning('请先选择应用');
            return;
        }

        try {
            const res = await getAuthUrl({ suite_id: suiteId });
            if (res.code === 0 && res.data && res.data.auth_url) {
                setAuthUrl(res.data.auth_url);
                setAuthModalVisible(true);
                setPolling(true);
            } else {
                message.error(res.message || '获取授权链接失败');
            }
        } catch (error) {
            console.error('Get auth url failed:', error);
            message.error('获取授权链接失败');
        }
    };


    const handleViewProgress = (record) => {
        setProgressRecord(record);
        setProgressModalVisible(true);
    };

    // Polling for auth completion
    useEffect(() => {
        let interval;
        if (polling && authModalVisible) {
            // Store initial state or just check for difference
            // Here we simply check if list changes. 
            // Simple strategy: check if total increases or list data string changes.
            // But we need to define "change".
            // Let's rely on total count increase for NEW install.
            // For re-auth, it's harder.
            // Since user request says "Scan completion... refresh list".
            // Basic assumption: If I am installing, total + 1.
            // If I am fixing auth, suite_auth_status changes.

            // Let's implement a simple check: if total != currentTotal (likely >)

            interval = setInterval(async () => {
                try {
                    // Fetch list silently
                    const res = await request.get('/corp/get_list', {
                        params: {
                            page: currentPage,
                            page_size: pageSize,
                            suite_id: suiteId,
                            ...filters
                        }
                    });
                    // Remove undefined filters
                    Object.keys(res.config?.params || {}).forEach(key => res.config?.params[key] === undefined && delete res.config?.params[key]);

                    if (res.code === 0) {
                        // Check if total changed (new install)
                        const newTotal = res.data.total;
                        // Or check if any auth status changed (re-auth) - complex.
                        // User requirement: "Scan completion... refresh list".

                        if (newTotal > total) {
                            clearInterval(interval);
                            handleAuthSuccess();
                        }
                    }
                } catch (e) {
                    console.error('Polling failed', e);
                }
            }, 3000);
        }
        return () => clearInterval(interval);
    }, [polling, authModalVisible, total, currentPage, pageSize, suiteId, filters]);

    const handleAuthSuccess = () => {
        setPolling(false);
        setAuthLoading(true);
        setTimeout(() => {
            setAuthLoading(false);
            setAuthModalVisible(false);
            message.success('授权成功');
            fetchData(currentPage, pageSize);
        }, 1000);
    };

    const handleCancelAuth = () => {
        setAuthModalVisible(false);
        setPolling(false);
        setAuthUrl('');
        fetchData(currentPage, pageSize);
    };



    const AuthDetailContent = ({ record }) => {
        const [qrCodeUrl, setQrCodeUrl] = useState('https://work.weixin.qq.com/localwebapp/loginpage_wx');
        const [qrLoading, setQrLoading] = useState(false);

        const handleRefreshQr = () => {
            setQrLoading(true);
            // Simulate refresh delay
            setTimeout(() => {
                setQrCodeUrl(`https://work.weixin.qq.com/localwebapp/loginpage_wx?t=${Date.now()}`);
                setQrLoading(false);
            }, 500);
        };

        const items = [
            { label: '组织架构', checked: record?.arch_auth_status === 1 },
            { label: '外部联系人', checked: record?.externalcontact_auth_status === 1 },
            { label: '通讯录', checked: record?.contact_auth_status === 1 },
            { label: '获客助手', checked: record?.customer_assistant_auth === 1 },
            { label: '可见范围', checked: (record?.view_range === 1 || record?.view_range === 2 || record?.view_range === 3) && record?.view_range_detail },
        ];

        return (
            <div style={{ padding: '0 20px' }}>
                <div style={{ marginBottom: 20, textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                    {isAuthorized(record) ? <span style={{ color: '#52c41a' }}>已全部授权</span> : <span style={{ color: '#ff4d4f' }}>存在未完成授权</span>}
                </div>
                {items.map((item, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid #f0f0f0', paddingBottom: 12 }}>
                        <span style={{ width: '100px', fontWeight: 'bold' }}>{item.label}：</span>
                        {item.checked ? (
                            <Tag icon={<CheckCircleOutlined />} color="success">已授权</Tag>
                        ) : (
                            <Tag icon={<CloseCircleOutlined />} color="error">未授权</Tag>
                        )}
                    </div>
                ))}

                {!isAuthorized(record) && (
                    <div style={{ marginTop: 24, padding: '16px', background: '#f5f5f5', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ marginBottom: 16, color: '#666' }}>
                            使用 <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{record?.corp_name}</span> 企微管理员扫码配置相关授权
                        </div>
                        <div>
                            <Button type="primary" href="https://work.weixin.qq.com/localwebapp/loginpage_wx" target="_blank">
                                前往扫码
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderAuthStatusList = (record) => {
        return <AuthDetailContent record={record} />;
    };

    const columns = [
        {
            title: '企微信息',
            dataIndex: 'corp_name',
            key: 'corp_name',
            fixed: 'left',
            width: 320,
            render: (text, record) => (
                <div className="corp-info">
                    <div className="corp-avatar">
                        {record.corp_square_logo_url ? (
                            <img src={record.corp_square_logo_url} alt="logo" style={{ width: '100%', height: '100%', borderRadius: '4px' }} />
                        ) : (
                            text ? text.charAt(0) : '-'
                        )}
                    </div>
                    <div className="corp-details">
                        <span className="corp-name">{text || '-'}</span>
                        <span className="corp-id" style={{ display: 'flex', alignItems: 'center' }}>
                            {record.corp_id}
                            <CopyOutlined
                                style={{ marginLeft: 4, cursor: 'pointer', color: '#1890ff' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(record.corp_id);
                                }}
                            />
                        </span>
                    </div>
                </div>
            ),
        },
        {
            title: '应用授权状态',
            align: 'center',
            dataIndex: 'suite_auth_status',
            key: 'suite_auth_status',
            width: 120,
            render: (status) => (
                <div>
                    <span className={`status-dot ${status === 1 ? 'success' : 'error'}`}></span>
                    <span className={`status-text ${status === 1 ? 'success' : 'error'}`}>
                        {status === 1 ? '已授权' : '解除授权'}
                    </span>
                </div>
            )
        },
        {
            title: (
                <span>
                    发布状态
                    <Tooltip title="授权完成后，系统会自动走发布流程：待开发->开发中->已上线">
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', cursor: 'help' }} />
                    </Tooltip>
                </span>
            ),
            align: 'center',
            dataIndex: 'release_status', // Updated from config_progress
            key: 'release_status',
            width: 120,
            render: (val) => {
                // 0=待开发; 1=开发中; 2=已上线; 3=存在未上线版本
                const statusMap = {
                    0: { text: '待开发', color: 'default' },
                    1: { text: '开发中', color: 'processing' }, // Blue
                    2: { text: '已发布', color: 'success' },
                    3: { text: '待发布', color: 'warning' },
                };
                const status = statusMap[val] || { text: '-', color: 'default' };
                return <Tag color={status.color}>{status.text}</Tag>;
            }
        },
        {
            title: (
                <span>
                    授权信息
                    <Tooltip title="发布状态=已发布，可设置相关授权">
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', cursor: 'help' }} />
                    </Tooltip>
                </span>
            ),
            key: 'auth_info',
            align: 'center',
            width: 120,
            render: (_, record) => {
                // release_status: 2 = 已发布 (Published)
                if (record.release_status !== 2) {
                    return '-';
                }

                if (isAuthorized(record)) {
                    return (
                        <div style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleViewProgress(record)}>
                            <span style={{ color: '#fa8c16' }}>已配置</span>
                        </div>
                    );
                } else {
                    // Calculate incomplete count
                    let missingCount = 0;
                    if (record.arch_auth_status !== 1) missingCount++;
                    if (record.externalcontact_auth_status !== 1) missingCount++;
                    if (record.contact_auth_status !== 1) missingCount++;
                    if (record.customer_assistant_auth !== 1) missingCount++;
                    if (!((record.view_range === 1 || record.view_range === 2 || record.view_range === 3) && record.view_range_detail)) missingCount++;

                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                backgroundColor: '#fff1f0',
                                border: '1px solid #ffccc7',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: 4,
                                color: '#f5222d',
                                fontSize: '12px'
                            }}>
                                <div style={{
                                    backgroundColor: '#ff4d4f',
                                    color: '#fff',
                                    borderRadius: '50%',
                                    width: '14px',
                                    height: '14px',
                                    lineHeight: '14px',
                                    textAlign: 'center',
                                    fontSize: '10px'
                                }}>{missingCount}</div>
                                <span style={{ marginLeft: 4 }}>配置中</span>
                            </div>
                            <div
                                style={{ color: '#fa8c16', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}
                                onClick={() => handleViewProgress(record)}
                            >
                                查看进度
                            </div>
                        </div>
                    );
                }
            }
        },
        {
            title: '获客助手概况',
            key: 'customer_assistant_overview',
            align: 'center',
            width: 150,
            render: (_, record) => {
                if (record.customer_assistant_errcode) {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                backgroundColor: '#fff1f0',
                                border: '1px solid #ffccc7',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: 4,
                                color: '#f5222d',
                                fontSize: '12px'
                            }}>
                                <span>缺少相关资质</span>
                            </div>
                            <a
                                href={record.customer_assistant_errurl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#fa8c16', fontSize: '12px', textDecoration: 'underline' }}
                            >
                                查看详情
                            </a>
                        </div>
                    );
                }
                return (
                    <div style={{ fontSize: '12px', display: 'inline-block', textAlign: 'left' }}>
                        <div>累计使用量：{record.customer_assistant_total || 0}</div>
                        <div>当前剩余量：{record.customer_assistant_balance || 0}</div>
                    </div>
                );
            }
        },
        {
            title: '企业认证',
            align: 'center',
            dataIndex: 'corp_type',
            key: 'corp_type',
            width: 120,
            render: (type) => type === 1 ? '已认证' : '未认证'
        },
        {
            title: '企业信息',
            key: 'corp_info',
            width: 200,
            render: (_, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div>规模: {record.corp_scale || '-'}</div>
                    <div>行业: {record.corp_industry ? `${record.corp_industry}/${record.corp_sub_industry || ''}` : '-'}</div>
                    {/* 认证信息已在'企业认证'列展示，但此处保留以符合聚合展示需求 */}
                </div>
            )
        },
        {
            title: '授权时间',
            dataIndex: 'create_at',
            key: 'create_at',
            width: 180,
            render: (create_at) => create_at || '-'
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 200,
            render: (_, record) => {
                const authorized = isAuthorized(record) && record.suite_auth_status === 1;
                return (
                    <span>
                        <span
                            className="action-link"
                            style={authorized ? {} : { color: '#ccc', cursor: 'not-allowed' }}
                            onClick={() => authorized && handleMemberManage(record)}
                        >
                            成员管理
                        </span>
                        <Button
                            type="link"
                            className="action-link"
                            onClick={() => handleSync(record)}
                            disabled={syncLoading || !authorized}
                            icon={syncLoading ? <SyncOutlined spin /> : null}
                            style={{ padding: 0, marginLeft: 10 }}
                        >
                            {syncLoading ? '更新中' : '同步企微信息'}
                        </Button>
                    </span>
                );
            },
        },
    ];

    return (
        <Spin spinning={syncLoading} tip="更新中...">
            <div className="wecom-list-container">
                <div className="wecom-header">
                    <span>企微管理</span>
                    <div>
                        <Button className="orange-button" onClick={handleAuthClick}>发起授权</Button>
                        <Button style={{ marginLeft: 10 }} onClick={() => setGuideDrawerVisible(true)}>授权流程说明</Button>
                    </div>
                </div>

                <Alert
                    message={
                        <span>
                            更新/同步企业信息：更新企微信息和员工信息。不要频繁操作，最快可每1分钟可同步更新一次！（只有 <span style={{ color: 'red', fontWeight: 'bold' }}> 发布状态 = 已发布，授权信息 = 已配置 </span>可点击同步企微信息）
                        </span>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                <div className="search-area">
                    <Input
                        className="search-item"
                        placeholder="输入企微ID"
                        allowClear
                        value={filters.corp_id}
                        onChange={e => setFilters({ ...filters, corp_id: e.target.value })}
                    />
                    <Select
                        className="search-item"
                        placeholder="选择应用授权状态"
                        allowClear
                        value={filters.suite_auth_status}
                        onChange={val => setFilters({ ...filters, suite_auth_status: val })}
                    >
                        <Option value={1}>已授权</Option>
                        <Option value={0}>未授权</Option>
                    </Select>

                    <div className="search-buttons">
                        <Button type="primary" className="orange-button" onClick={handleSearch}>查询</Button>
                        <Button onClick={handleReset}>重置</Button>
                    </div>
                </div>

                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="corp_id"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 1600 }}
                />

                <div className="pagination-container">
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={total}
                        onChange={onPageChange}
                        showSizeChanger
                        showQuickJumper
                        showTotal={total => `共 ${total} 条`}
                    />
                </div>

                <EmployeeList
                    visible={employeeDrawerVisible}
                    onClose={() => setEmployeeDrawerVisible(false)}
                    corpRecord={activeCorpRecord}
                />

                <Modal
                    title="企业微信授权"
                    open={authModalVisible}
                    onCancel={handleCancelAuth}
                    footer={null}
                    centered
                    width={400}
                >
                    {authLoading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}>
                            <Spin size="large" tip="授权成功，加载中..." />
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center' }}>
                            {/* <div style={{ marginBottom: 16, marginTop: 16 }}>
                                <div style={{ display: 'flex', marginBottom: 8 }}>
                                    <span style={{ color: '#999', width: '90px', textAlign: 'right', flexShrink: 0 }}>应用模版：</span>
                                    <span style={{ textAlign: 'left', flex: 1 }}>{currentSuite?.suite_name || '-'}</span>
                                </div>
                                <div style={{ display: 'flex' }}>
                                    <span style={{ color: '#999', width: '90px', textAlign: 'right', flexShrink: 0 }}>模版ID：</span>
                                    <span style={{ textAlign: 'left', flex: 1 }}>{currentSuite?.suite_id || '-'}</span>
                                </div>
                            </div> */}

                            <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: 16, marginBottom: 16 }}>
                                <span style={{ color: '#ff0000' }}>请使用企业微信管理员扫码</span>
                                <Popover content={<img src={`${BASE_URL}/pic/create_auth.png`} alt="示例图片" style={{ width: '400px' }} />} title="示例图片">
                                    <span style={{ color: '#1890ff', marginLeft: '10px', cursor: 'pointer' }}>查看示例</span>
                                </Popover>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                                {authUrl && (
                                    <QRCodeSVG value={authUrl} size={200} />
                                )}
                            </div>

                        </div>
                    )}
                </Modal>

                <Modal
                    title="授权详情"
                    open={progressModalVisible}
                    onCancel={() => setProgressModalVisible(false)}
                    footer={null}
                    centered
                    width={600}
                >
                    {progressRecord && renderAuthStatusList(progressRecord)}
                </Modal>

                <Drawer
                    title="授权流程说明"
                    placement="right"
                    onClose={() => setGuideDrawerVisible(false)}
                    open={guideDrawerVisible}
                    width={500}
                >
                    <Timeline>
                        <Timeline.Item>
                            <h4 style={{ fontWeight: 'bold', marginBottom: 8 }}> + 发起授权</h4>
                            <p style={{ marginLeft: 16 }}>1. 需企业微信管理员扫码，发起授权。</p>
                        </Timeline.Item>
                        <Timeline.Item>
                            <h4 style={{ fontWeight: 'bold', marginBottom: 8 }}> + 配置方式一：企微管理员扫码登录网页</h4>
                            <p style={{ marginLeft: 16 }}>当发布状态 = 已发布，可点击《授权信息》列的查看进度，扫码登录后进行相关授权配置。</p>
                        </Timeline.Item>
                        <Timeline.Item>
                            <h4 style={{ fontWeight: 'bold', marginBottom: 8 }}> + 配置方式二：管理员在企微App完成相关操作</h4>
                            <p style={{ marginLeft: 16 }}>1. 当发布状态 = 开发中，扫码授权的管理员需注意企业微信推送的系统消息，需管理员手动确认权限变更。</p>
                            <p style={{ marginLeft: 16 }}>2. 基本权限变更：</p>
                            <div style={{ marginLeft: 32, marginBottom: 12 }}>
                                <Image
                                    src="https://wecom.wzxn.top/pic/change_auth.png"
                                    alt="基本权限变更"
                                    width={150}
                                    preview={{
                                        mask: '放大',
                                    }}
                                    style={{ border: '1px solid #f0f0f0', borderRadius: '4px' }}
                                />
                            </div>
                            <p style={{ marginLeft: 16 }}>3. 获客助手授权：</p>
                            <div style={{ marginLeft: 32, marginBottom: 12 }}>
                                <Image
                                    src="https://wecom.wzxn.top/pic/hoke.png"
                                    alt="获客助手授权"
                                    width={150}
                                    preview={{
                                        mask: '放大',
                                    }}
                                    style={{ border: '1px solid #f0f0f0', borderRadius: '4px' }}
                                />
                            </div>
                            <p style={{ marginLeft: 16 }}>3. 应用可见范围配置：一般2小时内发布状态流转为：已发布，需企微管理员在企业微信应用“可见范围”配置</p>
                            <div style={{ marginLeft: 32, marginBottom: 12 }}>
                                <Image
                                    src="https://wecom.wzxn.top/pic/kejm.png"
                                    alt="可见范围配置"
                                    width={150}
                                    preview={{
                                        mask: '放大',
                                    }}
                                    style={{ border: '1px solid #f0f0f0', borderRadius: '4px' }}
                                />
                            </div>
                        </Timeline.Item>
                    </Timeline>
                </Drawer>
            </div>
        </Spin>
    );
};

export default WeComList;
